import * as React from "react";
import { Info } from "lucide-react";
import { Box, Button, HStack } from "@chakra-ui/react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  formatExecutionCandleTypeLabel,
  isExecutionCandleTypeDisabledForBarBudget,
  normalizeExecutionCandleType,
} from "@/utils/executionCandleTimeframe";
import {
  TRADE_REVIEW_ALERT_CLASS,
  TRADE_REVIEW_ALERT_DESC_CLASS,
} from "./tradeReviewAlertUi";

export function ExecutionCandlesHistoryLimitBanner(props: {
  message: string;
  suggestedCandleTypes: string[];
  currentCandleType: string;
  barBudgetMinCandleType?: string | null;
  onSelectCandleType: (candleType: string) => void;
  disabled?: boolean;
}): React.ReactElement {
  const currentNormalized = normalizeExecutionCandleType(props.currentCandleType);
  const quickSwitchOptions = React.useMemo(() => {
    const seen = new Set<string>();
    const options: Array<{ value: string; label: string; disabled: boolean }> = [];

    for (const raw of props.suggestedCandleTypes) {
      const normalized = normalizeExecutionCandleType(raw);
      if (!normalized || seen.has(normalized)) continue;
      seen.add(normalized);
      if (normalized === currentNormalized) continue;
      options.push({
        value: normalized,
        label: formatExecutionCandleTypeLabel(normalized),
        disabled: isExecutionCandleTypeDisabledForBarBudget(
          normalized,
          props.barBudgetMinCandleType
        ),
      });
    }

    return options;
  }, [
    props.barBudgetMinCandleType,
    props.currentCandleType,
    props.suggestedCandleTypes,
    currentNormalized,
  ]);

  return (
    <Alert variant="info" className={TRADE_REVIEW_ALERT_CLASS}>
      <Info aria-hidden />
      <AlertDescription className={TRADE_REVIEW_ALERT_DESC_CLASS}>
        <span className="block">{props.message}</span>
        {quickSwitchOptions.length > 0 ? (
          <Box mt={3}>
            <Box as="span" display="block" fontSize="xs" mb={2} opacity={0.9}>
              Try a coarser timeframe:
            </Box>
            <HStack spacing={2} flexWrap="wrap">
              {quickSwitchOptions.map((option) => (
                <Button
                  key={option.value}
                  size="xs"
                  variant="outline"
                  colorScheme="cyan"
                  isDisabled={props.disabled === true || option.disabled}
                  onClick={() => props.onSelectCandleType(option.value)}
                >
                  {option.label}
                </Button>
              ))}
            </HStack>
          </Box>
        ) : null}
      </AlertDescription>
    </Alert>
  );
}
